import { useCallback, useEffect, useState } from "react";

import { App as CapacitorApp } from "@capacitor/app";

import { AdhdOnboardingScreen } from "./components/adhd-onboarding-screen";
import { AdminScreen } from "./components/admin-screen";
import { ChildrenScreen } from "./components/children-screen";
import { CravingScreen } from "./components/craving-screen";
import { DashboardScreen } from "./components/dashboard-screen";
import { FavoritesScreen } from "./components/favorites-screen";
import { ForgotPasswordScreen } from "./components/forgot-password-screen";
import { HomeMaybeADHD } from "./components/home-maybe-adhd";
import { InviteAcceptScreen } from "./components/invite-accept-screen";
import { AndroidDownloadScreen } from "./components/android-download-screen";
import { LandingPage } from "./components/landing-page";
import { MealBarcodeScreen } from "./components/meal-barcode-screen";
import { MealConfirmationScreen } from "./components/meal-confirmation-screen";
import { MealEntryScreen } from "./components/meal-entry-screen";
import { MealPhotoScreen } from "./components/meal-photo-screen";
import { MealVoiceScreen } from "./components/meal-voice-screen";
import { MenuPhotoScreen } from "./components/menu-photo-screen";
import { OnboardingScreen } from "./components/onboarding-screen";
import { PantryAddScreen } from "./components/pantry-add-screen";
import { PantryPhotoConfirmScreen } from "./components/pantry-photo-confirm-screen";
import { PantryPhotoScreen } from "./components/pantry-photo-screen";
import { PantryScreen } from "./components/pantry-screen";
import { PrivacyScreen } from "./components/privacy-screen";
import { RecipesScreen } from "./components/recipes-screen";
import { ResetPasswordScreen } from "./components/reset-password-screen";
import { ScreenShell } from "./components/screen-shell";
import { ShoppingAddScreen } from "./components/shopping-add-screen";
import { ShoppingListScreen } from "./components/shopping-list-screen";
import { SignInScreen } from "./components/sign-in-screen";
import { TargetsScreen } from "./components/targets-screen";
import { TermsScreen } from "./components/terms-screen";
import { AddSheet } from "./components/ui/add-sheet";
import { BottomNav } from "./components/ui/bottom-nav";
import { VerifyEmailScreen } from "./components/verify-email-screen";
import {
  ApiError,
  type ParseMealView,
  type ParsedPantryView,
  type TargetsView,
  api,
} from "./lib/api";
import { AuthProvider, useAuth } from "./lib/auth-context";
import { I18nProvider, useLang } from "./lib/i18n";
import { isNativePlatform } from "./lib/native";
import { ProfileProvider } from "./lib/profile-context";
import { readReferralCode, writeReferralCode } from "./lib/referral";

type Route =
  | { kind: "home" }
  | { kind: "landing" }
  | { kind: "signin" }
  | { kind: "signup" }
  | { kind: "invite"; code: string }
  | { kind: "verify-email"; code: string }
  | { kind: "reset-password"; code: string }
  | { kind: "forgot-password" }
  | { kind: "targets" }
  | { kind: "children" }
  | { kind: "household" }
  | { kind: "meal-entry" }
  | { kind: "meal-photo" }
  | { kind: "meal-voice" }
  | { kind: "meal-barcode" }
  | { kind: "meal-confirm"; parsed: ParseMealView }
  | { kind: "pantry" }
  | { kind: "pantry-add" }
  | { kind: "pantry-photo" }
  | { kind: "pantry-photo-confirm"; parsed: ParsedPantryView }
  | { kind: "recipes" }
  | { kind: "shopping" }
  | { kind: "shopping-add" }
  | { kind: "craving" }
  | { kind: "menu" }
  | { kind: "favorites" }
  | { kind: "privacy" }
  | { kind: "terms" }
  | { kind: "android-download" }
  | { kind: "admin" };

const PATH_HOUSEHOLD_INVITE = "/invite/";
const PATH_REFERRAL = "/r/";

function readRoute(): Route {
  if (typeof window === "undefined") return { kind: "home" };
  const path = window.location.pathname;
  const search = new URLSearchParams(window.location.search);

  // Personal referral link /r/<code> → store code in sessionStorage and
  // route to the landing page so the banner can render. Existing
  // /invite/<code> remains the household-join URL.
  if (path.startsWith(PATH_REFERRAL)) {
    const code = path.slice(PATH_REFERRAL.length).split("/")[0]?.trim().toUpperCase();
    if (code) {
      writeReferralCode(code);
      // Replace the URL so reloads don't re-trigger writeReferralCode (and
      // the user sees a clean /). The banner reads from sessionStorage.
      window.history.replaceState({}, "", "/");
      return { kind: "landing" };
    }
  }
  if (path.startsWith(PATH_HOUSEHOLD_INVITE)) {
    const code = path.slice(PATH_HOUSEHOLD_INVITE.length).split("/")[0]?.trim().toUpperCase();
    if (code) return { kind: "invite", code };
  }
  if (path === "/verify-email") {
    const code = search.get("code");
    if (code) return { kind: "verify-email", code };
  }
  if (path === "/reset-password") {
    const code = search.get("code");
    if (code) return { kind: "reset-password", code };
  }
  if (path === "/forgot-password") return { kind: "forgot-password" };
  if (path === "/signin") return { kind: "signin" };
  if (path === "/signup") return { kind: "signup" };
  if (path === "/profile/children" || path === "/children") return { kind: "children" };
  if (path === "/privacy") return { kind: "privacy" };
  if (path === "/terms") return { kind: "terms" };
  if (path === "/android") return { kind: "android-download" };
  return { kind: "home" };
}

function clearRoute(): void {
  if (typeof window !== "undefined") {
    window.history.replaceState({}, "", "/");
  }
}

// Whether the one-time ADHD-mode onboarding question has been shown to this
// user. The ADHD preference itself persists server-side; this localStorage
// flag only records that we already asked, so the question shows exactly
// once (ADR-0015 §"Onboarding and discoverability").
const ADHD_ASKED_PREFIX = "sytofood.adhdAsked.";

function adhdAsked(userId: string): boolean {
  if (typeof window === "undefined") return true;
  try {
    return window.localStorage.getItem(ADHD_ASKED_PREFIX + userId) === "1";
  } catch {
    return true; // storage blocked — never block the user on the question
  }
}

function markAdhdAsked(userId: string): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(ADHD_ASKED_PREFIX + userId, "1");
  } catch {
    // storage blocked — the question may show again, acceptable degradation
  }
}

// Maps each route to where Android's system back-button should send the
// user — either another route, or "exit" to close the app. The mapping is
// "every sub-screen falls back to home; home / unauthenticated entry
// points exit". This is intentionally not history-based: the hand-rolled
// state routing doesn't push history entries, so reconstructing a stack
// would lie. Each screen already has its own "Wróć" CTA for finer
// navigation; this handler only catches the catch-all OS gesture so
// it never silently closes the app.
function backDestination(route: Route): Route | "exit" {
  switch (route.kind) {
    // Entry points / unauthenticated roots — system back exits the app.
    case "home":
    case "landing":
    case "signin":
    case "signup":
      return "exit";
    // Auth recovery flow — back goes to sign-in.
    case "forgot-password":
      return { kind: "signin" };
    // Everything else collapses to home (signed-in default surface).
    case "invite":
    case "verify-email":
    case "reset-password":
    case "targets":
    case "children":
    case "household":
    case "meal-entry":
    case "meal-photo":
    case "meal-voice":
    case "meal-barcode":
    case "meal-confirm":
    case "pantry":
    case "pantry-add":
    case "pantry-photo":
    case "pantry-photo-confirm":
    case "recipes":
    case "shopping":
    case "shopping-add":
    case "craving":
    case "menu":
    case "favorites":
    case "privacy":
    case "terms":
    case "android-download":
    case "admin":
      return { kind: "home" };
  }
}

function Router() {
  const { state } = useAuth();
  const { lang } = useLang();
  const [route, setRoute] = useState<Route>(() => readRoute());
  const [targets, setTargets] = useState<TargetsView | null>(null);
  const [targetsLoading, setTargetsLoading] = useState(true);
  const [trackerReloadKey, setTrackerReloadKey] = useState(0);
  const [addSheetOpen, setAddSheetOpen] = useState(false);
  // Re-render trigger after the one-time ADHD onboarding is answered/skipped.
  const [adhdAskedTick, setAdhdAskedTick] = useState(0);

  useEffect(() => {
    const onPop = () => setRoute(readRoute());
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  // Android system back button (gesture or 3-button nav). Without this
  // handler Capacitor lets the OS close the app immediately, even from
  // sub-screens — user expectation is "back goes back in app", and only
  // exits when there's nothing left to pop. Web/iOS skip the listener
  // (iOS has no system back, web has browser back via popstate above).
  useEffect(() => {
    if (!isNativePlatform()) return;
    let cleanup: (() => void) | null = null;
    const register = async () => {
      const handle = await CapacitorApp.addListener("backButton", () => {
        // Most-recently-opened UI first: floating add-sheet overlays every
        // route, so it takes precedence over the route's own back logic.
        if (addSheetOpen) {
          setAddSheetOpen(false);
          return;
        }
        const dest = backDestination(route);
        if (dest === "exit") {
          void CapacitorApp.exitApp();
          return;
        }
        setRoute(dest);
      });
      cleanup = () => {
        void handle.remove();
      };
    };
    void register();
    return () => {
      if (cleanup) cleanup();
    };
  }, [route, addSheetOpen]);

  const refreshTargets = useCallback(async () => {
    setTargetsLoading(true);
    try {
      const t = await api.getTargets();
      setTargets(t);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setTargets(null);
      }
    } finally {
      setTargetsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (state.status === "signed-in" && state.user.activeHouseholdId) {
      refreshTargets();
    } else {
      setTargets(null);
      setTargetsLoading(false);
    }
  }, [state, refreshTargets]);

  const goHome = () => {
    clearRoute();
    setRoute({ kind: "home" });
  };

  const goForgotPassword = () => {
    if (typeof window !== "undefined") {
      window.history.pushState({}, "", "/forgot-password");
    }
    setRoute({ kind: "forgot-password" });
  };

  const goPrivacy = () => {
    if (typeof window !== "undefined") {
      window.history.pushState({}, "", "/privacy");
    }
    setRoute({ kind: "privacy" });
  };
  const goTerms = () => {
    if (typeof window !== "undefined") {
      window.history.pushState({}, "", "/terms");
    }
    setRoute({ kind: "terms" });
  };

  if (state.status === "loading" || (state.status === "signed-in" && targetsLoading)) {
    const loading = { pl: "ładuję", en: "loading", de: "lädt" }[lang];
    const moment = { pl: "Chwilę", en: "One moment", de: "Einen Moment" }[lang];
    return (
      <ScreenShell title={loading}>
        <p className="text-body text-ink-500 text-center">{moment}</p>
      </ScreenShell>
    );
  }

  if (route.kind === "reset-password") {
    return <ResetPasswordScreen code={route.code} onDone={goHome} />;
  }

  // Email verification must work regardless of auth state. The link is
  // commonly opened on a different device than signup (phone vs laptop),
  // in incognito, or after the session cookie expired — in all those cases
  // the user is signed-out. The POST /auth/verify-email endpoint is public,
  // so the only thing needed is to render this screen before the
  // signed-out branch falls through to the landing page.
  if (route.kind === "verify-email") {
    return (
      <VerifyEmailScreen
        code={route.code}
        onDone={() => {
          if (state.status === "signed-in") {
            goHome();
          } else {
            // Verified but not signed-in here: send them to sign-in so
            // they can log in on this device.
            window.history.replaceState({}, "", "/signin");
            setRoute({ kind: "signin" });
          }
        }}
      />
    );
  }

  if (route.kind === "privacy") {
    return <PrivacyScreen onBack={goHome} />;
  }
  if (route.kind === "terms") {
    return <TermsScreen onBack={goHome} />;
  }
  if (route.kind === "android-download") {
    return <AndroidDownloadScreen onBack={goHome} />;
  }
  if (route.kind === "admin") {
    return <AdminScreen onBack={goHome} />;
  }

  if (state.status === "signed-out") {
    if (route.kind === "forgot-password") {
      return <ForgotPasswordScreen onBack={goHome} />;
    }
    if (route.kind === "signin" || route.kind === "signup") {
      return (
        <SignInScreen
          initialMode={route.kind === "signup" ? "sign-up" : "sign-in"}
          onForgotPassword={goForgotPassword}
          onOpenPrivacy={goPrivacy}
          onOpenTerms={goTerms}
        />
      );
    }
    // Default for "/" and "/landing" and unknown paths when signed-out:
    // show the marketing LP. SignIn is reachable via the CTA buttons.
    return (
      <LandingPage
        referralCode={readReferralCode()}
        onSignIn={() => {
          window.history.pushState({}, "", "/signin");
          setRoute({ kind: "signin" });
        }}
        onSignUp={() => {
          window.history.pushState({}, "", "/signup");
          setRoute({ kind: "signup" });
        }}
        onOpenPrivacy={goPrivacy}
        onOpenTerms={goTerms}
      />
    );
  }

  // signed-in
  if (route.kind === "invite") {
    return <InviteAcceptScreen code={route.code} onDone={goHome} />;
  }
  if (!state.user.activeHouseholdId) {
    return <OnboardingScreen />;
  }
  if (!targets || targets.kcalTarget === null) {
    return (
      <TargetsScreen
        {...(targets ? { initial: targets } : {})}
        onSaved={() => {
          refreshTargets();
          setRoute({ kind: "home" });
        }}
      />
    );
  }
  if (route.kind === "targets") {
    return (
      <TargetsScreen
        initial={targets}
        onSaved={() => {
          refreshTargets();
          setRoute({ kind: "home" });
        }}
        onCancel={() => setRoute({ kind: "home" })}
      />
    );
  }
  if (route.kind === "children") {
    return (
      <ChildrenScreen
        householdId={state.user.activeHouseholdId}
        onBack={() => setRoute({ kind: "household" })}
      />
    );
  }
  if (route.kind === "household") {
    return (
      <>
        <DashboardScreen
          householdId={state.user.activeHouseholdId}
          onBack={() => setRoute({ kind: "home" })}
          onAccountDeleted={() => {
            window.location.href = "/";
          }}
          onOpenPrivacy={goPrivacy}
          onOpenTerms={goTerms}
          onOpenTargets={() => setRoute({ kind: "targets" })}
          onOpenAdmin={() => setRoute({ kind: "admin" })}
          onOpenChildren={() => setRoute({ kind: "children" })}
        />
        <BottomNav
          active="me"
          onHome={() => setRoute({ kind: "home" })}
          onFridge={() => setRoute({ kind: "pantry" })}
          onBulb={() => setRoute({ kind: "craving" })}
          onMe={() => setRoute({ kind: "household" })}
          onAdd={() => setAddSheetOpen(true)}
        />
        <AddSheet
          open={addSheetOpen}
          onClose={() => setAddSheetOpen(false)}
          onText={() => setRoute({ kind: "meal-entry" })}
          onPhoto={() => setRoute({ kind: "meal-photo" })}
          onVoice={() => setRoute({ kind: "meal-voice" })}
          onBarcode={() => setRoute({ kind: "meal-barcode" })}
          onMenu={() => setRoute({ kind: "menu" })}
          onFavorites={() => setRoute({ kind: "favorites" })}
        />
      </>
    );
  }
  if (route.kind === "meal-entry") {
    return (
      <MealEntryScreen
        onParsed={(parsed) => setRoute({ kind: "meal-confirm", parsed })}
        onCancel={() => setRoute({ kind: "home" })}
      />
    );
  }
  if (route.kind === "meal-photo") {
    return (
      <MealPhotoScreen
        onParsed={(parsed) => setRoute({ kind: "meal-confirm", parsed })}
        onCancel={() => setRoute({ kind: "home" })}
      />
    );
  }
  if (route.kind === "meal-voice") {
    return (
      <MealVoiceScreen
        onParsed={(parsed) => setRoute({ kind: "meal-confirm", parsed })}
        onCancel={() => setRoute({ kind: "home" })}
      />
    );
  }
  if (route.kind === "meal-barcode") {
    return (
      <MealBarcodeScreen
        onParsed={(parsed) => setRoute({ kind: "meal-confirm", parsed })}
        onCancel={() => setRoute({ kind: "home" })}
      />
    );
  }
  if (route.kind === "pantry") {
    return (
      <>
        <PantryScreen
          householdId={state.user.activeHouseholdId}
          onAddManual={() => setRoute({ kind: "pantry-add" })}
          onAddPhoto={() => setRoute({ kind: "pantry-photo" })}
          onOpenRecipes={() => setRoute({ kind: "recipes" })}
          onOpenShopping={() => setRoute({ kind: "shopping" })}
        />
        <BottomNav
          active="fridge"
          onHome={() => setRoute({ kind: "home" })}
          onFridge={() => setRoute({ kind: "pantry" })}
          onBulb={() => setRoute({ kind: "craving" })}
          onMe={() => setRoute({ kind: "household" })}
          onAdd={() => setAddSheetOpen(true)}
        />
        <AddSheet
          open={addSheetOpen}
          onClose={() => setAddSheetOpen(false)}
          onText={() => setRoute({ kind: "meal-entry" })}
          onPhoto={() => setRoute({ kind: "meal-photo" })}
          onVoice={() => setRoute({ kind: "meal-voice" })}
          onBarcode={() => setRoute({ kind: "meal-barcode" })}
          onMenu={() => setRoute({ kind: "menu" })}
          onFavorites={() => setRoute({ kind: "favorites" })}
        />
      </>
    );
  }
  if (route.kind === "shopping") {
    return (
      <ShoppingListScreen
        householdId={state.user.activeHouseholdId}
        onAdd={() => setRoute({ kind: "shopping-add" })}
        onBack={() => setRoute({ kind: "home" })}
      />
    );
  }
  if (route.kind === "shopping-add") {
    return (
      <ShoppingAddScreen
        householdId={state.user.activeHouseholdId}
        onSaved={() => setRoute({ kind: "shopping" })}
        onCancel={() => setRoute({ kind: "shopping" })}
      />
    );
  }
  if (route.kind === "craving") {
    return (
      <>
        <CravingScreen
          householdId={state.user.activeHouseholdId}
          targets={targets}
          onLogged={() => {
            setTrackerReloadKey((k) => k + 1);
            setRoute({ kind: "home" });
          }}
          onUseAsMeal={(parsed) => setRoute({ kind: "meal-confirm", parsed })}
          onBack={() => setRoute({ kind: "home" })}
        />
        <BottomNav
          active="bulb"
          onHome={() => setRoute({ kind: "home" })}
          onFridge={() => setRoute({ kind: "pantry" })}
          onBulb={() => setRoute({ kind: "craving" })}
          onMe={() => setRoute({ kind: "household" })}
          onAdd={() => setAddSheetOpen(true)}
        />
        <AddSheet
          open={addSheetOpen}
          onClose={() => setAddSheetOpen(false)}
          onText={() => setRoute({ kind: "meal-entry" })}
          onPhoto={() => setRoute({ kind: "meal-photo" })}
          onVoice={() => setRoute({ kind: "meal-voice" })}
          onBarcode={() => setRoute({ kind: "meal-barcode" })}
          onMenu={() => setRoute({ kind: "menu" })}
          onFavorites={() => setRoute({ kind: "favorites" })}
        />
      </>
    );
  }
  if (route.kind === "menu") {
    return (
      <MenuPhotoScreen
        householdId={state.user.activeHouseholdId}
        targets={targets}
        onLogged={() => {
          setTrackerReloadKey((k) => k + 1);
          setRoute({ kind: "home" });
        }}
        onUseAsMeal={(parsed) => setRoute({ kind: "meal-confirm", parsed })}
        onBack={() => setRoute({ kind: "home" })}
      />
    );
  }
  if (route.kind === "favorites") {
    return (
      <FavoritesScreen
        householdId={state.user.activeHouseholdId}
        onLogged={() => {
          setTrackerReloadKey((k) => k + 1);
          setRoute({ kind: "home" });
        }}
        onBack={() => setRoute({ kind: "home" })}
      />
    );
  }
  if (route.kind === "recipes") {
    return (
      <RecipesScreen
        householdId={state.user.activeHouseholdId}
        onLogged={() => {
          setTrackerReloadKey((k) => k + 1);
          setRoute({ kind: "home" });
        }}
        onUseAsMeal={(parsed) => setRoute({ kind: "meal-confirm", parsed })}
        onBack={() => setRoute({ kind: "pantry" })}
      />
    );
  }
  if (route.kind === "pantry-add") {
    return (
      <PantryAddScreen
        householdId={state.user.activeHouseholdId}
        onSaved={() => setRoute({ kind: "pantry" })}
        onCancel={() => setRoute({ kind: "pantry" })}
      />
    );
  }
  if (route.kind === "pantry-photo") {
    return (
      <PantryPhotoScreen
        householdId={state.user.activeHouseholdId}
        onParsed={(parsed) => setRoute({ kind: "pantry-photo-confirm", parsed })}
        onCancel={() => setRoute({ kind: "pantry" })}
      />
    );
  }
  if (route.kind === "pantry-photo-confirm") {
    return (
      <PantryPhotoConfirmScreen
        householdId={state.user.activeHouseholdId}
        parsed={route.parsed}
        onSaved={() => setRoute({ kind: "pantry" })}
        onCancel={() => setRoute({ kind: "pantry-photo" })}
      />
    );
  }
  if (route.kind === "meal-confirm") {
    const source = route.parsed.source;
    const back =
      source === "photo"
        ? { kind: "meal-photo" as const }
        : source === "voice"
          ? { kind: "meal-voice" as const }
          : source === "barcode"
            ? { kind: "meal-barcode" as const }
            : { kind: "meal-entry" as const };
    return (
      <MealConfirmationScreen
        householdId={state.user.activeHouseholdId}
        parsed={route.parsed}
        onSaved={() => {
          setTrackerReloadKey((k) => k + 1);
          setRoute({ kind: "home" });
        }}
        onCancel={() => setRoute(back)}
      />
    );
  }
  // One-time ADHD-mode onboarding: shown on the Home route, after targets
  // are set, only when the feature flag is on and we have not asked this
  // user yet (ADR-0015). `adhdAskedTick` forces a re-render once answered.
  void adhdAskedTick;
  if (route.kind === "home" && state.user.adhdModeAvailable && !adhdAsked(state.user.id)) {
    return (
      <AdhdOnboardingScreen
        onDone={() => {
          markAdhdAsked(state.user.id);
          setAdhdAskedTick((n) => n + 1);
        }}
      />
    );
  }

  return (
    <>
      <HomeMaybeADHD
        householdId={state.user.activeHouseholdId}
        targets={targets}
        reloadKey={trackerReloadKey}
        onFridge={() => setRoute({ kind: "pantry" })}
        onBulb={() => setRoute({ kind: "craving" })}
        onMe={() => setRoute({ kind: "household" })}
        onAdd={() => setAddSheetOpen(true)}
        onMealLogged={() => setTrackerReloadKey((k) => k + 1)}
        onOpenChildren={() => setRoute({ kind: "children" })}
      />
      <AddSheet
        open={addSheetOpen}
        onClose={() => setAddSheetOpen(false)}
        onText={() => setRoute({ kind: "meal-entry" })}
        onPhoto={() => setRoute({ kind: "meal-photo" })}
        onVoice={() => setRoute({ kind: "meal-voice" })}
        onBarcode={() => setRoute({ kind: "meal-barcode" })}
        onMenu={() => setRoute({ kind: "menu" })}
        onFavorites={() => setRoute({ kind: "favorites" })}
      />
    </>
  );
}

export function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        {/* ProfileProvider (ADR-0018) lives INSIDE the AuthProvider so it can
            key the active-profile localStorage on the signed-in userId, and
            OUTSIDE the Router so a navigation never tears the context down. */}
        <ProfileProvider>
          <Router />
        </ProfileProvider>
      </AuthProvider>
    </I18nProvider>
  );
}
